import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ILineOfCreditResponseType } from 'src/types/LineOfCredit';
import { sdk } from 'src/utilities/sdk';
import { InvoiceState } from 'src/components/EnterpriseInvoices/enterpriseInvoices.utils';
import { IErrorResponse } from 'src/utilities/shared-types';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { IOrdersHistory } from 'src/types/OrderHistory';
import { MAX_NUMBER_OF_CHILD_ACCOUNTS } from 'src/components/enterprise/Parents/constants';
import { ParticipantTypes } from 'src/components/enterprise/set-up/steps/ManageEmployeesModal';
import { CORP_SIGN_UP_TYPE } from 'src/components/AuthProvider/AuthProvider';
import { ILocation } from 'src/types/Location';
import { ROLES } from 'src/types/Employee';

export const UNASSIGNED_DEPARTMENT_NAME = 'Unassigned';

export interface IDiner {
  id: string;
  diner_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  phone_number?: string;
  internal_id?: string;
  corp_diner_settings?: {
    sso_unique_identifier: string | null;
  };
}

export interface IDepartment {
  id: string;
  owner_id: string;
  name: string;
  active: boolean;
  deletable: boolean;
  employee_count: number;
}

export interface ILocDiner {
  id: string;
  name: string;
  email: string;
  member: boolean;
}

export interface IGroup {
  id: string;
  name: string;
  description: string;
  diner_count: number;
}

export interface IInvoice {
  amount_owed: number;
  cc_payment_date: string;
  csv_url: string;
  detailed_pdf_url: string;
  due_date: string;
  eligible_for_ccpayment: boolean;
  end: string;
  error_message: string;
  invoice_amount: number;
  invoice_date: string;
  invoice_id: string;
  invoice_number: string;
  invoiced_entity_id: string;
  invoiced_entity_name: string;
  invoicing_method: string;
  pdf_url: string;
  record_no: string;
  start: string;
  state: InvoiceState;
}

export interface IChildrenCorpResponse {
  id: string;
  name: string;
  client_id: string;
  firm_designator: string;
}

export interface ICorporateAccountsResponse {
  name: string;
  id: string;
  sign_up_type: CORP_SIGN_UP_TYPE;
}

export interface IParentAccountsResponse {
  name: string;
  parent_id: string;
  child_count: number;
}

export const PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export { SHORT_SEARCH_DELAY, LONG_SEARCH_DELAY } from 'src/utilities/delay';

export enum LineOfCreditSearchType {
  ACTIVE = 'ONLY_ACTIVE',
  INACTIVE = 'ONLY_INACTIVE',
}

function getFirstItemNumberFromPage(
  size: number,
  currentPage: number,
  totalItems: number
): number {
  const firstItemNumberFromPage = size * (currentPage - 1) + 1;
  if (totalItems > 0) {
    return firstItemNumberFromPage;
  }
  return 0;
}

function getLastItemNumberFromPage(
  size: number,
  currentPage: number,
  totalItems: number
): number {
  const lastItemNumberFromPage = size * currentPage;
  if (lastItemNumberFromPage > totalItems) {
    return totalItems;
  }
  return lastItemNumberFromPage;
}

export interface ISearchResults<T> {
  resultsList: T[];
  totalPages: number;
  currentSearchPage: number;
  nextSearchPage: number;
  totalResults: number;
  loadMoreNumber: number;
  hasMoreResults: boolean;
  searchStatus: SearchStatus;
  firstItemNumberFromPage: number;
  lastItemNumberFromPage: number;
  isUsingQuery: boolean;
  hasNoResultsWithQuery: boolean;
  hasNoResultsWithoutQuery: boolean;
  doSearch?: (
    params?: ISearchParams,
    isLoadingMore?: boolean,
    filters?: IFilterParams
  ) => Promise<void>;
  unsubscribeFromSearch?: () => void;
  subscribeToSearch?: () => void;
  error?: IErrorResponse | SdkErrorResponse;
}

export interface ISearchParams {
  query?: string;
  page?: number;
  size?: number;
  sort_field?: string;
  sort_order?: string;
  diner_role?: ROLES; // specific for Diner search
  filter_by?: string; // specific for LOC search
  include_diners_not_in_tag?: boolean; // specific for Diner search for LOC
  includeDinerCount?: boolean; // specific for Group search,
  dinerId?: string; // search corp accounts a diner is admin of
}

export interface IFilterParams {
  [f: string]: string | string[];
}

export enum SearchStatus {
  LOADING,
  IDLE,
  COMPLETE,
  ERROR,
}

export type SearchResourceOptions =
  | typeof useDepartmentSearch
  | typeof useDinerSearch
  | typeof useLineOfCreditSearch
  | typeof useLocationSearch
  | typeof useGroupSearch
  | typeof useOneCorpPeopleSearch;

const defaultSearchResults = {
  resultsList: [],
  totalPages: 1,
  currentSearchPage: 1,
  nextSearchPage: 1,
  totalResults: 0,
  loadMoreNumber: 0,
  hasMoreResults: false,
  searchStatus: SearchStatus.IDLE,
  firstItemNumberFromPage: 0,
  lastItemNumberFromPage: 0,
  hasNoResultsWithQuery: false,
  hasNoResultsWithoutQuery: false,
  isUsingQuery: false,
};

export function useOrderSearch(
  corpId: string,
  pageSize: number = PAGE_SIZE
): ISearchResults<IOrdersHistory> {
  const orderSearchUrl = `/corps/${corpId}/orders`;

  const searchParam: Partial<ISearchParams> = {
    sort_field: 'WHEN_FOR',
    sort_order: 'DESC',
    size: pageSize,
  };

  return useSearch<IOrdersHistory>(orderSearchUrl, searchParam, 'post');
}

export function useGroupSearch(
  corpId: string,
  includeDinerCount?: boolean
): ISearchResults<IGroup> {
  const groupSearchUrl = `/corps/${corpId}/tags/search`;

  const searchParam: Partial<ISearchParams> = {
    sort_field: 'NAME',
    sort_order: 'ASC',
    size: PAGE_SIZE,
  };

  if (includeDinerCount) {
    searchParam.includeDinerCount = true;
  }

  return useSearch<IGroup>(groupSearchUrl, searchParam);
}

export function useDepartmentSearch(
  corpId: string,
  removeUnassignedDepartment?: boolean
): ISearchResults<IDepartment> {
  const departmentSearchUrl = `/corps/${corpId}/departments/search`;

  const searchResults = useSearch<IDepartment>(departmentSearchUrl, {
    sort_field: 'NAME',
    sort_order: 'ASC',
    size: PAGE_SIZE,
  });

  // we need to adjust several properties when we don't
  // want to include the "Unassigned" department
  // TODO investigate if BE can add a param to filter this out
  if (removeUnassignedDepartment) {
    const { resultsList, isUsingQuery, totalResults } = searchResults;

    const filteredResultsList = resultsList.filter(
      (department) => department.name !== UNASSIGNED_DEPARTMENT_NAME
    );

    const isFiltered = filteredResultsList.length === resultsList.length - 1;

    const filteredTotalResults = isFiltered ? totalResults - 1 : totalResults;

    return {
      ...searchResults,
      resultsList: filteredResultsList,
      totalResults: filteredTotalResults,
      hasNoResultsWithoutQuery: isUsingQuery
        ? false
        : filteredTotalResults === 0,
      hasNoResultsWithQuery: isUsingQuery ? filteredTotalResults === 0 : false,
    };
  }

  return searchResults;
}

export function useLocationSearch(
  corpId: string,
  pageSize: number = PAGE_SIZE
): ISearchResults<ILocation> {
  const locationSearchUrl = `/corps/${corpId}/locations/search`;

  return useSearch<ILocation>(locationSearchUrl, {
    sort_field: 'NAME',
    sort_order: 'ASC',
    size: pageSize,
  });
}

export function useLocationEmployeesSearch(
  corpId: string,
  locationId: string
): ISearchResults<IDiner> {
  const searchUrl = `/corps/${corpId}/locations/${locationId}/diners/search`;

  return useSearch<IDiner>(searchUrl, {
    sort_field: 'LAST_NAME',
    sort_order: 'ASC',
    size: PAGE_SIZE,
  });
}

export function useInvoiceSearch(corpId: string): ISearchResults<IInvoice> {
  const invoicesSearchUrl = `/corps/${corpId}/invoice/list_for_corp_admin`;

  return useSearch<IInvoice>(invoicesSearchUrl, {
    size: PAGE_SIZE,
  });
}

export function useDinerSearch(
  corpId: string,
  params: ISearchParams = {}
): ISearchResults<IDiner> {
  const dinerSearchUrl = `/corps/${corpId}/diners/search`;

  return useSearch<IDiner>(dinerSearchUrl, {
    sort_field: 'LAST_NAME',
    sort_order: 'ASC',
    size: PAGE_SIZE,
    ...params,
  });
}

export function useLineOfCreditSearch(
  corpId: string,
  pageSize: number = PAGE_SIZE
): ISearchResults<ILineOfCreditResponseType> {
  const lineOfCreditSearchUrl = `/corps/${corpId}/eventmetadata/search`;

  return useSearch<ILineOfCreditResponseType>(lineOfCreditSearchUrl, {
    sort_field: 'NAME',
    sort_order: 'ASC',
    size: pageSize,
  });
}

export function useGroupDinersSearch(
  corpId: string,
  groupId: string,
  pageSize: number = PAGE_SIZE
): ISearchResults<ILocDiner> {
  // using the same endpoint
  return useLocDinersSearch(corpId, groupId, pageSize);
}

export function useLocDinersSearch(
  corpId: string,
  locId: string,
  pageSize: number = PAGE_SIZE
): ISearchResults<ILocDiner> {
  const locDinersSearchUrl = `/corps/${corpId}/diners/search/tag/${locId}`;

  return useSearch<ILocDiner>(locDinersSearchUrl, {
    sort_field: 'LAST_NAME',
    sort_order: 'ASC',
    size: pageSize,
  });
}

export function useChildCorpsSearch(
  parentCorpId: string,
  params: ISearchParams = {}
): ISearchResults<IChildrenCorpResponse> {
  const locDinersSearchUrl = `/corps/parentcorp/${parentCorpId}/children/search`;

  return useSearch<IChildrenCorpResponse>(locDinersSearchUrl, {
    page: 1,
    size: MAX_NUMBER_OF_CHILD_ACCOUNTS,
    sort_field: 'NAME',
    sort_order: 'ASC',
    ...params,
  });
}

export function useSearchCorporateAccountsByDinerId(
  dinerId: string,
  params: ISearchParams = {}
): ISearchResults<ICorporateAccountsResponse> {
  const corpAccountsSearchUrl = `/targetedaudiencesgateway/corp/admin/diner/${dinerId}/corps/search`;

  return useSearch<ICorporateAccountsResponse>(corpAccountsSearchUrl, {
    page: 1,
    size: MAX_PAGE_SIZE,
    sort_field: 'NAME',
    sort_order: 'ASC',
    ...params,
  });
}

export function useSearchParentAccountsByDinerId(
  dinerId: string,
  params: ISearchParams = {}
): ISearchResults<IParentAccountsResponse> {
  const parentAccountsSearchUrl = `/targetedaudiencesgateway/parent-corp/admin/diner/${dinerId}/corps/search`;

  return useSearch<IParentAccountsResponse>(parentAccountsSearchUrl, {
    page: 1,
    size: MAX_PAGE_SIZE,
    sort_field: 'NAME',
    sort_order: 'ASC',
    ...params,
  });
}

export function useOneCorpPeopleSearch(
  corpId: string
): ISearchResults<ILocDiner> {
  const { mealCreditId } = useParams<{ mealCreditId: string }>();

  const locDinersSearchUrl = `/corps/${corpId}/diners/search/tag/${mealCreditId}`;

  return useSearch<ILocDiner>(locDinersSearchUrl, {
    sort_field: 'LAST_NAME',
    sort_order: 'ASC',
    size: MAX_PAGE_SIZE,
    filter_by: ParticipantTypes.NON_MEMBERS_ONLY,
  });
}

export function useSearch<T>(
  searchUrl: string,
  defaultSearchParams: ISearchParams,
  httpMethodName: 'get' | 'post' = 'get'
): ISearchResults<T> {
  const [searchResults, setSearchResults] =
    useState<ISearchResults<T>>(defaultSearchResults);

  const previousQuery = useRef(undefined);
  const isSubscribed = useRef(true);

  function updateSearchResults(
    updatedResults: Partial<ISearchResults<T>>,
    isSubscribed: boolean
  ): void {
    if (isSubscribed) {
      setSearchResults((prevState) => ({
        ...prevState,
        ...updatedResults,
      }));
    }
  }

  function getLoadMoreNumber(
    numberOfResultsSoFar: number,
    totalResults: number
  ): number {
    const numberOfResultsLeft = totalResults - numberOfResultsSoFar;

    if (numberOfResultsLeft >= defaultSearchParams.size) {
      return defaultSearchParams.size;
    }

    return numberOfResultsLeft;
  }

  /* This unsubscribe method can be used to prevent memory leak (React no-op warning) when a component
   * unmounts before doSearch(async request) is complete. For example the selectSearchMenu, which is a dropdown menu
   * that populates menu item from an API call. The search request is fired off when the user opens the dropdown menu
   * and closes when the user clicks elsewhere.
   */
  function unsubscribeFromSearch() {
    isSubscribed.current = false;
  }

  /* This subscribe method is used in conjunction with the unsubscribeFromSearch and needs to be call before firing off a request(doSearch)
   * There are times where a subscription need to be re-established in order for a component to function correctly. For example, the selectSearchMenu
   * component(unsubscribe on unmount via unsubscribeFromSearch) needs to re-subscribed to doSearch because the search results are updated
   * when the user updated the search string.
   */
  function subscribeToSearch() {
    isSubscribed.current = true;
  }

  async function getHttpEndpoint(
    httpMethodName: 'get' | 'post',
    searchUrl: string,
    searchParams: ISearchParams,
    filters: IFilterParams
  ) {
    switch (httpMethodName) {
      case 'post':
        return sdk.http.post(searchUrl, filters, {
          searchParams,
        });
      case 'get':
      default:
        return sdk.http.get(searchUrl, {
          searchParams,
        });
    }
  }

  async function doSearch(
    currentSearchParams: ISearchParams = {},
    isLoadingMore?: boolean,
    filters: IFilterParams = {}
  ) {
    updateSearchResults(
      {
        searchStatus: SearchStatus.LOADING,
      },
      isSubscribed.current
    );

    try {
      const { nextSearchPage, resultsList } = searchResults;

      const searchParams = {
        ...defaultSearchParams,
        ...currentSearchParams,
      };

      const { page, query, size } = searchParams;

      let pageNumber = nextSearchPage;
      const hasNewQuery = query !== previousQuery.current;

      if (page) {
        pageNumber = page;
      } else if (hasNewQuery || !isLoadingMore) {
        pageNumber = 1;
      } else if (isLoadingMore) {
        // Only increase the page if it is explicitly loading more results
        pageNumber += 1;
      }
      searchParams.page = pageNumber;

      const {
        results,
        total_hits,
        total_pages,
        page: searchPage,
      } = await getHttpEndpoint(
        httpMethodName,
        searchUrl,
        searchParams,
        filters
      );

      previousQuery.current = query;

      const newResultsList =
        pageNumber === 1 ? results : [...resultsList, ...results];

      const loadMoreNumber = getLoadMoreNumber(
        newResultsList.length,
        total_hits
      );

      const isUsingQuery = !!query;
      const hasNoResults = results.length === 0;

      // Only concat results if it is explicitly loading more results
      updateSearchResults(
        {
          resultsList: isLoadingMore ? newResultsList : results,
          hasNoResultsWithQuery: hasNoResults && isUsingQuery,
          hasNoResultsWithoutQuery: hasNoResults && !isUsingQuery,
          isUsingQuery,
          totalPages: total_pages,
          currentSearchPage: searchPage,
          nextSearchPage: pageNumber,
          totalResults: total_hits,
          loadMoreNumber,
          hasMoreResults: isLoadingMore
            ? loadMoreNumber > 0
            : pageNumber < total_pages,
          searchStatus: SearchStatus.COMPLETE,
          firstItemNumberFromPage: getFirstItemNumberFromPage(
            size,
            pageNumber,
            total_hits
          ),
          lastItemNumberFromPage: getLastItemNumberFromPage(
            size,
            pageNumber,
            total_hits
          ),
        },
        isSubscribed.current
      );
    } catch (error) {
      updateSearchResults(
        {
          searchStatus: SearchStatus.ERROR,
          error,
        },
        isSubscribed.current
      );
    }
  }

  return {
    ...searchResults,
    doSearch,
    unsubscribeFromSearch,
    subscribeToSearch,
  };
}
